import type { Metadata } from 'next';
import { FRONTEND_ORIGIN, SITE } from '@/app/constants';
import type { SupportedLanguage } from '@/app/types';
import { generateLanguagesConfig } from './language';

const FALLBACK_ORIGIN = 'https://www.vidu.com';
const DEFAULT_SOCIAL_IMAGE = '/pwas/maskable-icon-512x512.png';

function getSiteOrigin() {
  return FRONTEND_ORIGIN || FALLBACK_ORIGIN;
}

function getStringTitle(title: Metadata['title']) {
  if (typeof title === 'string') return title;
  if (title && typeof title === 'object' && 'absolute' in title && typeof title.absolute === 'string') {
    return title.absolute;
  }
  return undefined;
}

function buildAbsolutePageUrl(path: string, locale: string) {
  const normalizedPath = path === '/' ? '' : path;
  const localePrefix = locale === 'en' ? '' : `/${locale}`;
  return `${getSiteOrigin()}${localePrefix}${normalizedPath || '/'}`;
}

function getOpenGraphType(openGraph: Metadata['openGraph']) {
  return openGraph && 'type' in openGraph ? openGraph.type : undefined;
}

function getTwitterCard(twitter: Metadata['twitter']) {
  return twitter && 'card' in twitter ? twitter.card : undefined;
}

function withDefaultSocialMetadata(path: string, metadata: Metadata, locale: string): Metadata {
  const title = getStringTitle(metadata.title);
  if (!title || !metadata.description) return metadata;

  const url = buildAbsolutePageUrl(path, locale);
  const image = `${getSiteOrigin()}${DEFAULT_SOCIAL_IMAGE}`;
  const defaultOpenGraphImage = { url: image, alt: title };

  return {
    ...metadata,
    openGraph: {
      ...metadata.openGraph,
      type: getOpenGraphType(metadata.openGraph) ?? 'website',
      url: metadata.openGraph?.url ?? url,
      title: metadata.openGraph?.title ?? title,
      description: metadata.openGraph?.description ?? metadata.description,
      images: metadata.openGraph?.images ?? [defaultOpenGraphImage],
    },
    twitter: {
      ...metadata.twitter,
      card: getTwitterCard(metadata.twitter) ?? 'summary_large_image',
      title: metadata.twitter?.title ?? title,
      description: metadata.twitter?.description ?? metadata.description,
      images: metadata.twitter?.images ?? [image],
    },
  };
}

/**
 * 生成带有规范链接和可选 hreflang 标签的元数据
 * @param path 页面路径
 * @param metadata 基础元数据
 * @param locale 当前语言
 * @param includeHreflang 是否包含 hreflang 标签，默认为 true
 * @param supportedLocales 支持的语言列表，默认使用 GLOBAL_LANGUAGES（全部语言）
 * @returns 完整的元数据对象
 */
export function generateCanonicalMetadata(
  path: string,
  metadata: Metadata,
  locale: string,
  includeHreflang = true,
  supportedLocales?: SupportedLanguage[],
): Metadata {
  const metadataWithSocial = withDefaultSocialMetadata(path, metadata, locale);
  const isChina = SITE === 'china';
  // 中国站点或禁用 hreflang 时，只返回规范链接
  if (isChina || !includeHreflang) {
    return {
      ...metadataWithSocial,
      // 解决携带 query，辨认成另外一个网站的问题
      alternates: {
        canonical: path,
      },
    };
  }

  // 全球站点且启用 hreflang 时，返回完整的语言标签
  return {
    ...metadataWithSocial,
    alternates: {
      canonical: `${locale === 'en' ? '' : locale}${path}`,
      languages: generateLanguagesConfig(path, supportedLocales),
    },
  };
}
